import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import { toast } from "react-toastify";
import loader from "../../methods/loader";
import Layout2 from "../../components/global/layout2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const categoryOptions = [
  { value: "onboarding", label: "Onboarding" },
  { value: "bid", label: "Bid" },
  { value: "load", label: "Load" },
  { value: "carrier", label: "Carrier" },
  { value: "driver", label: "Driver" },
  { value: "system", label: "System" },
  { value: "custom", label: "Custom" },
];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "draft", label: "Draft" },
];

const categoryVariables = {
  onboarding: [
    "carrier_name",
    "company_name",
    "email",
    "mc_number",
    "dot_number",
    "login_url",
    "status",
    "reason",
  ],
  bid: [
    "carrier_name",
    "load_number",
    "bid_amount",
    "bid_status",
    "load_origin",
    "load_destination",
    "expiration_date",
  ],
  load: [
    "load_number",
    "origin",
    "destination",
    "shipment_status",
    "carrier_name",
    "driver_name",
    "pickup_date",
    "delivery_date",
  ],
  carrier: ["carrier_name", "company_name", "note_description", "admin_name"],
  driver: ["driver_name", "carrier_name", "email", "password"],
  system: ["app_name", "support_email", "current_date"],
  custom: [],
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];

const AddEditEmailTemplate = () => {
  const { id } = useParams();
  const history = useHistory();
  const quillRef = useRef(null);

  const [form, setform] = useState({
    name: "",
    subject: "",
    body_html: "",
    variables: [],
    category: "onboarding",
    description: "",
    status: "draft",
  });

  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Load template data in edit mode
  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get("email-template", { id: id }).then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setform({
            name: d.name || "",
            subject: d.subject || "",
            body_html: d.body_html || "",
            variables: d.variables || [],
            category: d.category || "onboarding",
            description: d.description || "",
            status: d.status || "draft",
          });
        }
        loader(false);
      });
    }
  }, [id]);

  // Update variables list when category changes
  useEffect(() => {
    const vars = categoryVariables[form.category] || [];
    setform((prev) => ({ ...prev, variables: vars }));
  }, [form.category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!form.body_html.trim() || form.body_html === "<p><br></p>") {
      toast.error("Email body is required");
      return;
    }

    loader(true);
    const payload = { ...form };

    if (id) {
      payload.id = id;
      ApiClient.put("email-template", payload).then((res) => {
        if (res.success) {
          toast.success("Template updated successfully");
          history.push("/email-templates");
        }
        loader(false);
      });
    } else {
      ApiClient.post("email-template", payload).then((res) => {
        if (res.success) {
          toast.success("Template created successfully");
          history.push("/email-templates");
        }
        loader(false);
      });
    }
  };

  const insertVariable = useCallback(
    (varName) => {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        const position = range ? range.index : quill.getLength() - 1;
        quill.insertText(position, `{{${varName}}}`);
        quill.setSelection(position + varName.length + 4);
      }
    },
    []
  );

  const handlePreview = () => {
    loader(true);
    if (id) {
      // Use server-side preview for existing templates
      const sampleVars = {};
      (categoryVariables[form.category] || []).forEach((v) => {
        sampleVars[v] = `[Sample ${v.replace(/_/g, " ")}]`;
      });
      ApiClient.post("email-template/preview", {
        id: id,
        variables: sampleVars,
      }).then((res) => {
        if (res.success && res.data) {
          setPreviewHtml(res.data.html || res.data.body_html || "");
        } else {
          // Fallback to local preview
          generateLocalPreview();
        }
        setShowPreview(true);
        loader(false);
      });
    } else {
      generateLocalPreview();
      setShowPreview(true);
      loader(false);
    }
  };

  const generateLocalPreview = () => {
    let html = form.body_html;
    (categoryVariables[form.category] || []).forEach((v) => {
      const regex = new RegExp(`\\{\\{${v}\\}\\}`, "g");
      html = html.replace(
        regex,
        `<span style="background:#fff3cd;padding:1px 4px;border-radius:3px;">[${v.replace(
          /_/g,
          " "
        )}]</span>`
      );
    });
    setPreviewHtml(html);
  };

  const handleSendTest = () => {
    if (!testEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!id) {
      toast.error("Please save the template first before sending a test email");
      return;
    }
    loader(true);
    const sampleVars = {};
    (categoryVariables[form.category] || []).forEach((v) => {
      sampleVars[v] = `[Sample ${v.replace(/_/g, " ")}]`;
    });
    ApiClient.post("email-template/send-test", {
      id: id,
      to: testEmail,
      variables: sampleVars,
    }).then((res) => {
      if (res.success) {
        toast.success("Test email sent successfully");
        setShowTestModal(false);
        setTestEmail("");
      }
      loader(false);
    });
  };

  const availableVars = categoryVariables[form.category] || [];

  return (
    <Layout2 title="Email Templates">
      <div className="mainareas">
        <form onSubmit={handleSubmit}>
          <div className="edit-page-common new-edit edit-page-commons">
            {/* Header */}
            <div className="row">
              <div className="col-lg-12">
                <div className="title-head">
                  <h3 className="ViewUser mb-0 user-back">
                    <Link to="/email-templates" className="">
                      <i
                        className="fa fa-arrow-left ml-1"
                        title="Back"
                        aria-hidden="true"
                      ></i>
                    </Link>
                    {id ? "Edit" : "Add"} Email Template
                  </h3>
                </div>
              </div>
            </div>

            {/* Basic Details */}
            <div className="row pt-3">
              <div className="col-md-12">
                <div className="white-head mb-3">
                  <h5 className="profilelist">Template Details</h5>
                </div>
              </div>
              <div className="col-md-12">
                <div className="white-bg-main mb-4">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>
                        Template Name<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.name}
                        required
                        placeholder="e.g. Carrier Onboarding Welcome"
                        onChange={(e) =>
                          setform({ ...form, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>
                        Subject<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.subject}
                        required
                        placeholder="e.g. Welcome to OneBrokerage, {{carrier_name}}!"
                        onChange={(e) =>
                          setform({ ...form, subject: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label>
                        Category<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={form.category}
                        onChange={(e) =>
                          setform({ ...form, category: e.target.value })
                        }
                      >
                        {categoryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label>
                        Status<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={form.status}
                        onChange={(e) =>
                          setform({ ...form, status: e.target.value })
                        }
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label>Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.description}
                        placeholder="Brief description of when this template is used"
                        onChange={(e) =>
                          setform({ ...form, description: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Variable Helper */}
            {availableVars.length > 0 && (
              <div className="row">
                <div className="col-md-12">
                  <div className="white-head mb-3">
                    <h5 className="profilelist">
                      Available Variables{" "}
                      <small style={{ fontWeight: 400, color: "#888" }}>
                        (click to insert at cursor)
                      </small>
                    </h5>
                  </div>
                </div>
                <div className="col-md-12 mb-3">
                  <div
                    className="white-bg-main"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      padding: "16px",
                    }}
                  >
                    {availableVars.map((v) => (
                      <span
                        key={v}
                        onClick={() => insertVariable(v)}
                        style={{
                          display: "inline-block",
                          padding: "5px 12px",
                          backgroundColor: "#eef1f8",
                          border: "1px solid #d0d7e8",
                          borderRadius: "16px",
                          fontSize: "13px",
                          color: "#3E549D",
                          cursor: "pointer",
                          fontFamily: "monospace",
                          transition: "all 0.2s",
                          userSelect: "none",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#3E549D";
                          e.target.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#eef1f8";
                          e.target.style.color = "#3E549D";
                        }}
                        title={`Click to insert {{${v}}}`}
                      >
                        {"{{" + v + "}}"}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Rich Text Editor */}
            <div className="row">
              <div className="col-md-12">
                <div className="white-head mb-3">
                  <h5 className="profilelist">
                    Email Body<span className="text-danger">*</span>
                  </h5>
                </div>
              </div>
              <div className="col-md-12 mb-4">
                <div className="white-bg-main" style={{ padding: "16px" }}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.body_html}
                    onChange={(value) =>
                      setform({ ...form, body_html: value })
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ minHeight: "300px" }}
                    placeholder="Compose your email template here..."
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handlePreview}
                >
                  <i className="fa fa-eye me-1"></i> Preview
                </button>
                {id && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => setShowTestModal(true)}
                    style={{
                      borderColor: "#3E549D",
                      color: "#3E549D",
                    }}
                  >
                    <i className="fa fa-paper-plane me-1"></i> Send Test
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => history.push("/email-templates")}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {id ? "Update" : "Create"} Template
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Preview Modal */}
        {showPreview && (
          <div
            className="modal black-screen d-block"
            style={{ zIndex: 1050 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowPreview(false);
            }}
          >
            <div
              className="modal-dialog"
              style={{ maxWidth: "700px", marginTop: "5%" }}
            >
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{
                    borderBottom: "1px solid #eee",
                    padding: "16px 20px",
                  }}
                >
                  <h5 className="modal-title" style={{ fontWeight: 600 }}>
                    Template Preview
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPreview(false)}
                  ></button>
                </div>
                <div style={{ padding: "12px 20px", background: "#f8f9fa" }}>
                  <small style={{ color: "#666" }}>
                    <strong>Subject:</strong> {form.subject}
                  </small>
                </div>
                <div
                  className="modal-body"
                  style={{
                    padding: "20px",
                    maxHeight: "60vh",
                    overflowY: "auto",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: "4px",
                      padding: "20px",
                      backgroundColor: "#fff",
                    }}
                  ></div>
                </div>
                <div
                  className="modal-footer"
                  style={{
                    borderTop: "1px solid #eee",
                    padding: "12px 20px",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPreview(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Test Modal */}
        {showTestModal && (
          <div
            className="modal black-screen d-block"
            style={{ zIndex: 1050 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowTestModal(false);
            }}
          >
            <div className="modal-dialog" style={{ marginTop: "10%" }}>
              <div className="modal-content p-4">
                <div className="modal-body">
                  <h5 className="mb-3" style={{ fontWeight: 600 }}>
                    Send Test Email
                  </h5>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    Enter the email address to receive a test version of this
                    template. Variables will be replaced with sample data.
                  </p>
                  <div className="mb-3">
                    <label>
                      Email Address<span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSendTest();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="text-center d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowTestModal(false);
                      setTestEmail("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSendTest}
                  >
                    <i className="fa fa-paper-plane me-1"></i> Send Test
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout2>
  );
};

export default AddEditEmailTemplate;
